import "./style.scss";

import { getValueOrDraft, shouldDraft } from "components/Beneficiar/utils";
import ModalBox from "components/Modal";
import NewForm from "components/NewForm";
import { handleScrollToErrors } from "components/NewForm/utils";
import DeleteModal from "components/shared/DeleteModal";
import type { IChannel } from "containers/Channels/types";
import { formatCustomFields, formatFieldsForDefaultValues } from "containers/CustomFieldsContainer/helpers";
import type { IFormStep } from "containers/TermFormContainer";
import type { ITitle } from "containers/Titles/types";
import { PortalType } from "contexts/portal.context";
import { correctXMLText, formatDateToIso, getCleanStartorEndDay, isIsoDate } from "helpers/utils";
import { XMLRegexList } from "helpers/xmlRegexList-helpers";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchBrands } from "store/brandStore";
import { fetchCustomFields } from "store/customFieldsStore";
import { selectAllPriceHistory, selectChannelAllActivePriceHistory } from "store/priceHistory/priceHistory.selectors";
import { addDefaultEntry, discardDraft, initDraft, updateDraftEntry } from "store/priceHistory/priceHistoryStore";
import {
    fetchAnalyticalCodes,
    fetchCreateNewTitle,
    fetchDeleteTitle,
    fetchISBNTitleInfo,
    fetchTags,
    fetchUpdateTitle,
    sendTitleCover,
    setDeleteTitleIdleStatus,
} from "store/titleStore";
import type { TFetchStatus } from "store/types";
import { ChannelKind } from "store/types";
import { fetchWork, invalidateWorks } from "store/workStore";

import { useAppDispatch, useAppSelector } from "../../store/store";
import DilicomInformations from "./DilicomInformation";
import {
    getCollectionData,
    getFormatData,
    getImageData,
    getPageNumber,
    getPrice,
    getReleaseDate,
    getSummaryData,
    getTitleData,
} from "./FormatDilicomData/utils";
import GeneralInfoStep from "./GeneralInfoStep";
import { initialCheckInfo, initialDilicomInfo, initialHighlightField } from "./initialStates";
import LastYearInfoStep from "./LastYearInfoStep";
import NewAnalyticalCode from "./NewAnalyticalCode";
import OtherInformationsStep from "./OtherInformationsStep";
import PriceChannelsStep from "./PriceChannelsStep";
import type {
    OverriddenChannel,
    TCheckInfo,
    TDilicomInfo,
    TEan13DilicomOption,
    TFormCreateTitle,
    TInitialHighlightField,
    TNewTitle,
    TOnCollapseFields,
    TOverriddenChannelsData,
} from "./types";
import {
    formatStartingChannelsData,
    mergePriceHistory,
    toCustomTurnoverCumulativeCents,
    toQuantityCumulative,
} from "./utils";

interface ICreateTitle {
    /**
     * This prop is the name of the component mode.
     */
    mode: "new" | "edit-page";
    /**
     * This is the state to display editMode
     */
    editModeActive?: boolean;
    /**
     * Close edit mode
     */
    closeEditMode?: () => void;
}

const parsePositiveInt = (value: string | null): number | null => {
    if (!value) return null;
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const TitleFormContainer = ({ mode, editModeActive, closeEditMode }: ICreateTitle) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const portalType = useContext(PortalType);
    const isBookPortal = portalType === "book";
    const workIdFromQuery = parsePositiveInt(searchParams.get("workId"));
    const dilicomTitleData = useAppSelector((state) => state.titles.ISBNinfo);
    const {
        title,
        titleStatus,
        tags,
        codes,
        createTitleStatus,
        updateTitleStatus,
        sendPictureStatus,
        termAssociatedToTitle,
        deleteTitleStatus,
        dilicomFetchStatus,
    } = useAppSelector((state) => state.titles);
    const { brands } = useAppSelector((state) => state.brands);
    const { channels } = useAppSelector((state) => state.channels);
    const { customFields, customFieldsStatus } = useAppSelector((state) => state.customFields);
    const { works, selectedWork, selectedWorkStatus } = useAppSelector((state) => state.works);
    const lockedWork = useMemo(() => {
        if (workIdFromQuery == null) return null;
        return (
            works.find((work) => work.id === workIdFromQuery) ??
            (selectedWork?.id === workIdFromQuery ? selectedWork : null)
        );
    }, [selectedWork, workIdFromQuery, works]);

    const dataTitle: ITitle | null = useMemo(() => title, [titleStatus, title]);
    const isInEditMode = !!editModeActive && !!dataTitle;
    const titleWorkId = isInEditMode ? dataTitle?.workId ?? null : null;
    const titleWork = useMemo(() => {
        if (titleWorkId == null) return null;
        return (
            works.find((work) => work.id === titleWorkId) ?? (selectedWork?.id === titleWorkId ? selectedWork : null)
        );
    }, [selectedWork, titleWorkId, works]);

    useEffect(() => {
        if (workIdFromQuery == null) return;
        if (lockedWork != null) return;
        if (selectedWorkStatus === "pending") return;
        dispatch(fetchWork(workIdFromQuery));
    }, [dispatch, lockedWork, selectedWorkStatus, workIdFromQuery]);

    useEffect(() => {
        if (titleWorkId == null) return;
        if (titleWork != null) return;
        if (selectedWorkStatus === "pending") return;
        dispatch(fetchWork(titleWorkId));
    }, [dispatch, selectedWorkStatus, titleWork, titleWorkId]);

    const activePeriod = useAppSelector((state) => state.period.activePeriodData);
    const { draftEntries } = useAppSelector((state) => state.priceHistory);
    const priceHistoryEntries = useAppSelector(selectAllPriceHistory);
    const defaultActivePriceHistoryEntries = useAppSelector(
        selectChannelAllActivePriceHistory(undefined, activePeriod?.periodStart, activePeriod?.periodEnd),
    );

    const statuses = useMemo(
        () => ({
            createTitleStatus,
            updateTitleStatus,
            sendPictureStatus,
            titleStatus,
        }),
        [createTitleStatus, sendPictureStatus, updateTitleStatus, titleStatus],
    );

    const codeNames = useMemo(() => {
        return codes.map((code) => code.name);
    }, [codes]);
    const { getCompanySettings, companyInfos } = useCompanySettings();
    const usesMultipleBrand = getCompanySettings("uses_multiple_brands");
    const usesStocks = getCompanySettings("uses_stock");
    const hasDraftEAN13 = dataTitle?.ean13?.startsWith("draft_");

    const currentCustomFields = useMemo(() => {
        return customFields.filter((field) => field.relatedResource === "title");
    }, [customFields]);

    /** DILICOM DEBUG */
    // useEffect(() => {
    //     console.log("on edit data title :", dataTitle)
    //     console.log("dilicom data title :", dilicomTitleData)
    // }, [dataTitle, dilicomTitleData])

    /** REACT STATES */
    const [dilicomInfo, setDilicomInfo] = useState<TDilicomInfo>(initialDilicomInfo);
    const [ean13DilicomOption, setEan13DilicomOption] = useState<TEan13DilicomOption>([]);
    const [highlightField, setHighlightField] = useState<TInitialHighlightField>(initialHighlightField);
    const [openNewCodeModal, setOpenNewCodeModal] = useState(false);
    const [openInfoModal, setOpenInfoModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [checkInfo, setCheckInfo] = useState<TCheckInfo>(initialCheckInfo);
    const [newCode, setNewCode] = useState("");
    const [ean13Value, setEan13Value] = useState("");
    const [submitFormStatus, setSubmitFormStatus] = useState<TFetchStatus>("idle");
    const [activeChannels, setActiveChannels] = useState<number[]>([]);
    const [onCollapseFields, setOnCollapseFields] = useState<TOnCollapseFields>({
        specifications: false,
        adminInfo: false,
    });

    const formComponentRef = useRef(null);

    /**
     * @param {string} name field name
     * @param {boolean} value if checked or not
     */
    const onCheckInfo = (name: string, value: boolean) => {
        setCheckInfo((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    /**
     * Active a channel
     * @param {number} channelId id of the active channel (active toggle)
     */
    const onActiveChannels = (channelId: number) => {
        setActiveChannels((prevChannels) => [...prevChannels, channelId]);
    };

    /**
     * Reset highlight fields style
     * @param {string} fieldName field name
     */
    const onResetHighlight = (fieldName: string) => {
        setHighlightField((prev: any) => ({
            ...prev,
            [fieldName]: false,
        }));
    };

    const onCloseDilicomModal = () => {
        setOpenInfoModal(false);
        setCheckInfo(initialCheckInfo);
    };

    /**
     * Change collapse fields in other informations step
     * @param {string} category name of the collapse category
     */
    const showFields = (category: "specifications" | "adminInfo") => {
        setOnCollapseFields((prev) => ({
            ...prev,
            [category]: !prev[category],
        }));
    };

    /** 
     This function verify if the Dilicom details info has been checked (validate checkbox) by the user 
     + a condition to set the value to the input (exemple: dilicomInfo.title) or no ("")
     + a condition to set a color on the input, if it's auto-completed with state inputHighlight (boolean)
     @param {boolean} onValidate correspond to the validated information, ex : checkInfo.price 
     @param {string} fieldName the name of the input
     @param {string} info the information to put in the field, ex : dilicomInfo.price
     @param {Date} date the releaseDate of title because this info can't be a key string of object
     */
    const onValidateInfo = (
        onValidate: boolean,
        fieldName: keyof TFormCreateTitle,
        info: string | number | Date,
        date: Date,
    ) => {
        let information = info;
        if (fieldName === "releaseDate") information = date;

        if (onValidate) setValue(fieldName, information);
        if (info != "") {
            setHighlightField((prev: TInitialHighlightField) => ({
                ...prev,
                [fieldName]: onValidate,
            }));
            /** In other informations step, expand collapse for autocompleted inputs */
            if (fieldName === "category" || fieldName === "format" || fieldName === "pagination")
                showFields("specifications");
        }
    };

    const onSubmitAutoCompletion = () => {
        /** Convert string date into js date for the date picker */
        const splitDate: any = dilicomInfo.releaseDate.split("/");
        const date: Date = new Date(+splitDate[2], splitDate[1] - 1, +splitDate[0]);

        /** Validate Checkboxes */
        let key: keyof typeof checkInfo;
        for (key in checkInfo) {
            if (key === "price") continue;
            onValidateInfo(checkInfo[key], key, dilicomInfo[key], date);
        }

        /** Sync Dilicom price to the default price history entry */
        if (checkInfo.price && dilicomInfo.price) {
            defaultActivePriceHistoryEntries.map((d) =>
                dispatch(
                    updateDraftEntry({
                        ...d,
                        price: Math.round(+dilicomInfo.price.toString().replace(",", ".") * 100),
                    }),
                ),
            );
        }

        setValue("ean13", { ean13: ean13Value }, { shouldDirty: true });
        onCloseDilicomModal();
    };

    /* 
    SHOW TWO CHANNELS BY DEFAULT ON CREATE TITLE : One physical and one digital / if none of these just put 2 first channels
    */
    const defaultChannelsFields = useMemo(() => {
        let twoFirstChannels: IChannel[] = [];
        const physicalChannels = channels.filter((channel) => channel.kind === ChannelKind.PHYSICAL);
        const digitalChannels = channels.filter((channel) => channel.kind === ChannelKind.DIGITAL);

        if (channels.length == 1) {
            twoFirstChannels = channels;
        } else if (physicalChannels.length < 2 || digitalChannels.length < 2) {
            twoFirstChannels = channels.slice(0, 2);
        } else {
            twoFirstChannels = [physicalChannels[0], digitalChannels[0]];
        }

        const formatChannels: OverriddenChannel[] = [];

        twoFirstChannels.forEach((channel: IChannel) => {
            formatChannels.push({
                channelName: channel.name,
                name: "",
                releaseDate: null,
                ean13: "",
                countryMarket: "",
                channelId: channel.id,
                kind: channel.kind,
                persisted: true,
                calculationMethod: channel.calculationMethod,
                subscriptionCoefficient: 1,
            });
        });
        return formatChannels;
    }, [channels, companyInfos]);

    /** USEFORM INITIATE VALUES */
    const defaultFormValues: TFormCreateTitle = useMemo(() => {
        const defaultValues: TFormCreateTitle = {
            ean13: isInEditMode ? { ean13: getValueOrDraft(dataTitle?.ean13) || "" } : { ean13: "" },
            name: isInEditMode ? dataTitle?.name : "",
            releaseDate: isInEditMode
                ? dataTitle?.releaseDate
                    ? getCleanStartorEndDay(dataTitle.releaseDate)
                    : null
                : null,
            creationDate: isInEditMode
                ? dataTitle?.creationDate
                    ? new Date(dataTitle.creationDate)
                    : new Date()
                : new Date(),
            avatarUrl: isInEditMode ? dataTitle?.avatarUrl : "",
            tags: isInEditMode ? dataTitle?.tags : [],
            analysisCode: isInEditMode ? dataTitle?.analysisCode : "",
            shortNote: isInEditMode ? dataTitle?.shortNote : "",
            associatedBrand: isInEditMode
                ? { name: dataTitle?.associatedBrand ?? t("Titles.ByDefault") }
                : { name: t("Titles.ByDefault") },
            initialInventory: isInEditMode ? dataTitle?.initialInventory?.toString() ?? "" : "",
            category: isInEditMode ? dataTitle?.otherInformation?.category : "",
            distributorReference: isInEditMode ? dataTitle?.distributorReference : "",
            draw: isInEditMode ? dataTitle?.otherInformation?.draw : "",
            format: isInEditMode ? dataTitle?.otherInformation?.format : "",
            formatType:
                isInEditMode && dataTitle?.formatTypeId
                    ? { id: dataTitle.formatTypeId, name: dataTitle.formatTypeName ?? "" }
                    : null,
            pagination: isInEditMode ? dataTitle?.otherInformation?.pagination : "",
            prize: isInEditMode ? dataTitle?.otherInformation?.prize : "",
            summary: isInEditMode ? dataTitle?.otherInformation?.summary ?? "" : "",
            notes: isInEditMode ? dataTitle?.otherInformation?.notes ?? "" : "",
            sku: isInEditMode ? dataTitle?.sku ?? "" : "",
            // overriddenChannelsData come from useFormContext() and contain fields used in PriceChannelsStep component.
            overriddenChannelsData: isInEditMode
                ? dataTitle?.overriddenChannelsData.length > 0
                    ? dataTitle?.overriddenChannelsData?.map((channel) => {
                          return {
                              channelName: channel.channelName,
                              name: channel.name,
                              releaseDate: channel.releaseDate ? getCleanStartorEndDay(channel.releaseDate) : null,
                              ean13: channel.ean13,
                              countryMarket: channel.countryMarket || "",
                              channelId: channel.channelId,
                              kind: channel.kind,
                              persisted: true,
                              calculationMethod: channel.calculationMethod,
                              subscriptionCoefficient: channel.subscriptionCoefficient ?? 1,
                          };
                      })
                    : defaultChannelsFields
                : defaultChannelsFields, // --> here's the default displayed channels
            // startingChannelsData come from useFormContext() and contain fields used in LastYearInfoStep component
            startingChannelsData: formatStartingChannelsData(channels, dataTitle?.startingChannelsData, isInEditMode),
            nameTranslations: isInEditMode ? dataTitle?.nameTranslations : [],
            defaultPriceHistory: priceHistoryEntries.filter((p) => !p.channelId),
            work: null,
            workDisplayName: "",
        };

        // If there are custom fields created, add them to the default values:
        if (currentCustomFields.length > 0) {
            const customFields = formatFieldsForDefaultValues({
                customFields: currentCustomFields,
                editModeActive: isInEditMode,
                typeData: dataTitle,
            });
            defaultValues.customFields = customFields ?? [];
        }
        return defaultValues;
    }, [isInEditMode, dataTitle, t, defaultChannelsFields, channels, priceHistoryEntries, currentCustomFields]);

    useEffect(() => {
        setHighlightField(initialHighlightField);

        if (mode === "edit-page") {
            reset(defaultFormValues);
            if (defaultFormValues.ean13.ean13) setEan13Value(defaultFormValues.ean13.ean13);
        }
        // Active toggle channels while edit if title got channels data, then reset the value while exit edit mode
        if (isInEditMode) {
            if (dataTitle?.overriddenChannelsData.length > 0) {
                dataTitle?.overriddenChannelsData.map((channel: TOverriddenChannelsData) => {
                    onActiveChannels(channel.channelId);
                });
            }
        }
        if (!isInEditMode) setActiveChannels([]);
    }, [isInEditMode]);

    const { ...methods } = useForm({
        defaultValues: defaultFormValues,
        mode: "all",
    });

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        trigger,
        setError,
        formState: { errors, isDirty, dirtyFields },
        reset,
    } = methods;

    const currentCode = watch("analysisCode");
    const currentTitle = watch("name");

    useEffect(() => {
        if (workIdFromQuery == null || lockedWork == null) return;
        setValue("workDisplayName", lockedWork.name);
    }, [lockedWork, setValue, workIdFromQuery]);

    useEffect(() => {
        if (!isInEditMode || titleWork == null) return;
        const currentWork = watch("work");
        if (currentWork != null && currentWork.id !== dataTitle?.workId) return;
        setValue("work", titleWork, { shouldDirty: false });
    }, [dataTitle?.workId, isInEditMode, setValue, titleWork, watch]);

    useEffect(() => {
        if (currentCode?.id == -1) {
            setOpenNewCodeModal(true);
            setValue("analysisCode", "");
        }
    }, [currentCode]);

    const startingChannelsData = watch("startingChannelsData");

    useEffect(() => {
        // When refreshing form, startingChannelsData wouldn't be filled with default data.
        if (defaultFormValues.startingChannelsData.length !== startingChannelsData.length) {
            setValue(
                "startingChannelsData",
                formatStartingChannelsData(channels, dataTitle?.startingChannelsData, isInEditMode),
            );
        }
    }, [channels, startingChannelsData]);

    // When refreshing form, overridenChannelsData wouldn't be filled with default data.
    useEffect(() => {
        if (mode === "new" && defaultChannelsFields.length > 0 && !watch("overriddenChannelsData").length) {
            setValue("overriddenChannelsData", defaultChannelsFields);
        }
    }, [defaultChannelsFields, mode]);

    useEffect(() => {
        if (newCode !== "") {
            const code = codeNames?.find((name) => name === newCode);
            setValue("analysisCode", code);
        }
    }, [newCode, codes]);

    useEffect(() => {
        dispatch(fetchAnalyticalCodes());
        if (usesMultipleBrand) dispatch(fetchBrands(true));
    }, []);

    useEffect(() => {
        if (mode !== "new") return;
        dispatch(initDraft([]));
        dispatch(addDefaultEntry({ currency: companyInfos?.currency, price: (dataTitle?.price ?? 0) / 100 }));
        return () => {
            dispatch(discardDraft());
        };
    }, [companyInfos?.currency, dataTitle?.price, dispatch, mode]);

    useEffect(() => {
        if (mode !== "edit-page" || !isInEditMode || titleStatus !== "succeeded" || !dataTitle) return;
        dispatch(initDraft(priceHistoryEntries));
        return () => {
            dispatch(discardDraft());
        };
    }, [dataTitle?.id, dispatch, isInEditMode, mode, titleStatus]);

    useEffect(() => {
        if (mode === "new" && tags === null) dispatch(fetchTags());
    }, [tags]);

    /** DILICOM LOGIC PART --> */
    useEffect(() => {
        if (ean13Value.length === 13) {
            dispatch(fetchISBNTitleInfo({ ISBN: ean13Value }));
        }
    }, [ean13Value]);

    useEffect(() => {
        // Only set dilicomInfo once fetch done, otherwise we have a false moment with "no options" before the result appears.
        if (!["idle", "pending"].includes(dilicomFetchStatus) && dilicomTitleData.data) {
            setDilicomInfo({
                name: correctXMLText(XMLRegexList, getTitleData(dilicomTitleData)),
                releaseDate: getReleaseDate(dilicomTitleData),
                price: getPrice(dilicomTitleData),
                format: getFormatData(dilicomTitleData),
                pagination: getPageNumber(dilicomTitleData),
                avatarUrl: getImageData(dilicomTitleData),
                summary: correctXMLText(XMLRegexList, getSummaryData(dilicomTitleData)).trim(),
                category: getCollectionData(dilicomTitleData),
            });
        }
    }, [dilicomTitleData, dilicomFetchStatus]);

    useEffect(() => {
        if (dilicomInfo.name === "") return; // Need ean13DilicomOption to be empty to show the noOptionsText in the autocomplete.
        setEan13DilicomOption([
            {
                imgUrl: dilicomInfo.avatarUrl,
                title: dilicomInfo.name,
                ean13: ean13Value,
            },
        ]);
    }, [dilicomInfo]);

    /* ON SUBMIT FORM */
    const onSubmit = async (form: TFormCreateTitle) => {
        setSubmitFormStatus("pending");
        const {
            startingChannelsData,
            category,
            format,
            formatType,
            pagination,
            prize,
            draw,
            summary,
            avatarUrl,
            notes,
            customFields,
            work,
            ...req
        } = form;

        const avatarUrlValue = typeof avatarUrl === "string" ? avatarUrl : "";

        // Format channel data DTO and concat country market channels for compatibility
        const activeChannelsData = (overriddenChannelsValues: TOverriddenChannelsData[], activeChannels: number[]) => {
            const filteredChannels = overriddenChannelsValues.filter(
                (channel) => activeChannels.includes(channel.channelId) && !channel.countryMarket,
            );

            return filteredChannels.flatMap((channel) => {
                // Send UTC midnight of the local calendar day the user picked so the
                // backend stores the intended day regardless of the user's timezone.
                const normalizedReleaseDate =
                    channel.releaseDate && !isIsoDate(channel.releaseDate as string)
                        ? new Date(
                              Date.UTC(
                                  (channel.releaseDate as Date).getFullYear(),
                                  (channel.releaseDate as Date).getMonth(),
                                  (channel.releaseDate as Date).getDate(),
                              ),
                          )
                        : channel.releaseDate;
                const countryMarketChannels: TOverriddenChannelsData[] = (channel.priceHistory ?? [])
                    ?.flatMap((p) => p.childCurrencies)
                    .map(({ currency, country, price }) => ({
                        ...channel,
                        priceHistory: undefined,
                        countryMarket: country,
                        price,
                        currency,
                        releaseDate: normalizedReleaseDate,
                    }));

                return [
                    {
                        ...channel,
                        releaseDate: normalizedReleaseDate,
                    },
                    ...countryMarketChannels,
                ];
            });
        };

        // Same fallback chain as mergePriceHistory: without it, an unloaded or
        // missing active period would silently wipe the legacy price to 0.
        const defaultLegacyPriceEntry =
            defaultActivePriceHistoryEntries[0] ??
            draftEntries.find((e) => !e.channelId && e.endDate === null) ??
            draftEntries.find((e) => !e.channelId);

        const requestData: TNewTitle = {
            ...req,
            price: defaultLegacyPriceEntry?.price ?? 0,
            currency: defaultLegacyPriceEntry?.currency,
            name: req.name?.trim(),
            releaseDate: req.releaseDate ? formatDateToIso(req.releaseDate) : null,
            avatarUrl: avatarUrlValue,
            ean13: shouldDraft(dataTitle?.ean13 ?? "", ean13Value),
            overriddenChannelsData:
                activeChannelsData(
                    mergePriceHistory(form.overriddenChannelsData, draftEntries, activePeriod),
                    activeChannels,
                ) ?? [],
            defaultPriceHistory: draftEntries
                .filter((e) => !e.channelId)
                .map(({ id, ...e }) => (id < 0 ? e : { ...e, id })),
            otherInformation: { format, pagination, prize, draw, summary, category, notes },
            associatedBrand: usesMultipleBrand ? (req.associatedBrand as any)?.name : null,
            startingChannelsData: startingChannelsData.map((item) => ({
                channelId: item.channelId,
                quantity: toQuantityCumulative(item.quantity),
                turnover: toCustomTurnoverCumulativeCents(item.turnover),
                kind: item.kind,
                usesAlternativeMilestoneAmount: item.usesAlternativeMilestoneAmount,
                turnover_1: toCustomTurnoverCumulativeCents(item.turnover_1),
                turnover_2: toCustomTurnoverCumulativeCents(item.turnover_2),
            })),
            customFields: formatCustomFields(customFields, currentCustomFields),
        };

        if (isBookPortal) {
            requestData.formatTypeId = formatType?.id ?? null;
            if (mode === "new") {
                requestData.workId = work?.id ?? workIdFromQuery ?? undefined;
            } else if (work?.id) {
                requestData.workId = work.id;
            }
        }

        if (mode === "edit-page" && dataTitle) {
            /** fetch sendTitleCover only if user added a an image from Dilicom */
            if (avatarUrlValue.includes("images.centprod.com"))
                await dispatch(sendTitleCover({ url: avatarUrlValue })).then((resp: any) => {
                    requestData.avatarUrl = resp.payload;
                });
            const resp = await dispatch(fetchUpdateTitle({ data: requestData, id: dataTitle.id }));
            if (resp.meta.requestStatus === "fulfilled") {
                const nextWorkId = requestData.workId ?? dataTitle.workId;
                if (nextWorkId && nextWorkId !== dataTitle.workId) {
                    navigate(`/works/${nextWorkId}/titles/${dataTitle.id}`);
                }
                closeEditMode && closeEditMode();
                setSubmitFormStatus("succeeded");
                reset(); // used to reset isDirty after an edit on Title
            }

            if (resp.meta.requestStatus === "rejected") {
                setSubmitFormStatus("failed");
                if ((resp.payload as { url: string; message: string })?.message?.includes("title.ean_company")) {
                    setError("ean13", {
                        message: t("NewTitle.EAN13AlreadyExists"),
                        type: "ean13",
                    });
                }
            }
        }

        if (mode === "new") {
            /** fetch sendTitleCover only if user added a an image from Dilicom */
            if (avatarUrlValue.includes("images.centprod.com"))
                await dispatch(sendTitleCover({ url: avatarUrlValue })).then((resp: any) => {
                    requestData.avatarUrl = resp.payload;
                });

            const response = await dispatch(fetchCreateNewTitle({ data: requestData }));
            if (response.meta.requestStatus === "fulfilled") {
                dispatch(invalidateWorks());
                const createdTitleId = response.payload.result.result.createdTitleId;
                const workId = requestData.workId ?? workIdFromQuery ?? response.payload.result.result.createdWorkId;
                navigate(workId ? `/works/${workId}` : `/titles/${createdTitleId}`);
                setSubmitFormStatus("succeeded");
            }
            if (response.meta.requestStatus === "rejected") {
                if (
                    (response.payload as { message?: string } | undefined)?.message === "ServerError.TitleAlreadyExists"
                ) {
                    const formComponent = formComponentRef.current;
                    formComponent && (formComponent as any).forceStepChange(0);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    setError("ean13", { message: t("NewTitle.EAN13AlreadyExists") }, { shouldFocus: true });
                }
                setSubmitFormStatus("failed");
            }
        }
    };

    const handleDeleteTitle = async () => {
        if (!dataTitle) return;
        await dispatch(fetchDeleteTitle(dataTitle.id)).then(() => {
            dispatch(invalidateWorks());
            navigate(dataTitle.workId ? `/works/${dataTitle.workId}` : "/works");
        });
        setOpenDeleteModal(false);
        closeEditMode && closeEditMode();
    };

    useEffect(() => {
        if (deleteTitleStatus === "failed") {
            dispatch(setDeleteTitleIdleStatus());
        }
        if (deleteTitleStatus === "succeeded") {
            dispatch(setDeleteTitleIdleStatus());
        }
    }, [deleteTitleStatus, dispatch]);

    useEffect(() => {
        if (["idle", "pending"].includes(customFieldsStatus)) {
            dispatch(fetchCustomFields()).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    const newCustomFields = formatFieldsForDefaultValues({
                        customFields: resp.payload.filter((item) => item.relatedResource === "title"),
                        editModeActive: isInEditMode,
                        typeData: dataTitle,
                    });
                    setValue("customFields", newCustomFields);
                }
            });
        }
    }, [customFieldsStatus]);

    /* FORM STEPS COMPONENTS */
    const steps: IFormStep[] = [
        {
            stepTitle: t("NewTitle.GeneralInformation"),
            stepSubtitle: "",
            format: (stepTitle: string, stepSubtitle: string) => (
                <GeneralInfoStep
                    useFormData={{
                        control,
                        errors,
                        watch,
                        setValue,
                    }}
                    mode={mode}
                    editModeActive={isInEditMode}
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    indexStep={0}
                    setEan13Value={setEan13Value}
                    ean13Value={ean13Value}
                    setEan13DilicomOption={setEan13DilicomOption}
                    ean13DilicomOption={ean13DilicomOption}
                    highlightField={highlightField}
                    setOpenInfoModal={setOpenInfoModal}
                    onResetHighlight={onResetHighlight}
                    statuses={statuses}
                    customFields={currentCustomFields.filter((field) => field.formLocation === "generalInfo")}
                    hasDraftEAN13={hasDraftEAN13}
                    dilicomFetchStatus={dilicomFetchStatus}
                    workField={
                        !isBookPortal ? "hidden" : mode === "new" ? (workIdFromQuery ? "locked" : "select") : "select"
                    }
                    workExtraOption={titleWork}
                    isBookPortal={isBookPortal}
                />
            ),
        },
        {
            stepTitle: t("NewTitle.SpecificPrices"),
            stepSubtitle: t("NewTitle.SpecificPricesSubtitle"),
            format: (stepTitle: string, stepSubtitle: string) => (
                <PriceChannelsStep
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    indexStep={1}
                    activeChannels={activeChannels}
                    setActiveChannels={setActiveChannels}
                    highlightField={highlightField}
                    onResetHighlight={onResetHighlight}
                    dataTitle={dataTitle}
                />
            ),
        },
        {
            stepTitle: t("NewTitle.LastFinYearInformation"),
            stepSubtitle: "",
            format: (stepTitle: string, stepSubtitle: string) => (
                <LastYearInfoStep
                    useFormData={{ control, errors, watch }}
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    indexStep={2}
                    hasArchive={dataTitle?.hasArchive}
                    usesStocks={usesStocks}
                />
            ),
        },
        {
            stepTitle: t("NewTitle.OtherInformation"),
            stepSubtitle: "",
            format: (stepTitle: string, stepSubtitle: string) => (
                <OtherInformationsStep
                    useFormData={{ control, errors, watch, setValue }}
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    indexStep={3}
                    tagList={tags}
                    codeNames={codeNames}
                    brands={brands}
                    onCollapseFields={onCollapseFields}
                    mode={mode}
                    usesMultipleBrand={usesMultipleBrand}
                    highlightField={highlightField}
                    onResetHighlight={onResetHighlight}
                    showFields={showFields}
                    companyName={companyInfos?.name}
                    customFields={currentCustomFields.filter((field) => field.formLocation === "otherInfo")}
                    isBookPortal={isBookPortal}
                />
            ),
        },
    ];

    /* NAVIGATION STEP SUBTITLE */
    const dataForSteps = [`${currentTitle} <> ${ean13Value}`];

    /* ARCHIVE STEP */
    const deleteStep = {
        title: t("NewTitle.ArchiveTitle"),
        subtitle: t("NewTitle.ArchiveDesc"),
    };

    const deleteConfirmationDescription = (
        <>
            <p>{t("Titles.AssociatedContracts")}</p>
            <ul>
                <li>{t("Titles.LinkedData1")}</li>
                <li>{t("Titles.LinkedData2")}</li>
                <li>{t("Titles.LinkedData3")}</li>
            </ul>
        </>
    );

    return (
        <>
            {/** ADD ANALYTICAL CODE MODAL */}
            <ModalBox
                open={openNewCodeModal}
                onClose={() => setOpenNewCodeModal(false)}
                mainTitle={t("AnalyticalCode.AddAnalyticalCode")}
            >
                <NewAnalyticalCode
                    companyId={dataTitle?.companyId}
                    onClose={() => setOpenNewCodeModal(false)}
                    onCreate={(name: string) => {
                        setNewCode(name);
                    }}
                />
            </ModalBox>

            {/** DILICOM INFO TITLE MODAL */}
            <ModalBox
                open={openInfoModal}
                onClose={() => onCloseDilicomModal()}
                mainTitle={t("NewTitle.TitleDetails")}
                subTitle={t("NewTitle.ComprehensiveSheet")}
            >
                <DilicomInformations
                    dilicomInfo={dilicomInfo}
                    ean13Value={ean13Value}
                    checkInfo={checkInfo}
                    onCheckInfo={onCheckInfo}
                    onSubmitAutoCompletion={onSubmitAutoCompletion}
                />
            </ModalBox>

            {/** DELETE TITLE MODAL */}
            {isInEditMode && (
                <DeleteModal
                    open={openDeleteModal}
                    onClose={() => setOpenDeleteModal(false)}
                    onDelete={handleDeleteTitle}
                    elementTitle={dataTitle?.name ?? ""}
                    needsConfirmation={termAssociatedToTitle.length > 0}
                    confirmationText={t("Titles.DeleteTitleConfirmation")}
                    description={
                        termAssociatedToTitle.length > 0
                            ? deleteConfirmationDescription
                            : t("Titles.NoAssociatedContracts")
                    }
                />
            )}

            {/* This is the component that can be reused for every form. */}
            <FormProvider {...methods}>
                <div className="create-title-container">
                    <NewForm
                        ref={formComponentRef}
                        type="title"
                        steps={steps}
                        dataForSteps={dataForSteps}
                        onSubmit={onSubmit}
                        txtBtnSubmitNew={t("NewTitle.CreateTitle")}
                        mode={mode}
                        closeEditMode={closeEditMode}
                        deleteStep={deleteStep}
                        useFormData={{ trigger, handleSubmit, isDirty, dirtyFields }}
                        onDelete={() => setOpenDeleteModal(true)}
                        returnTo={t("NewTitle.ToTitles")}
                        onCloseRoute={
                            mode === "new" ? (workIdFromQuery ? `/works/${workIdFromQuery}` : "/works") : undefined
                        }
                        submitFormStatus={submitFormStatus}
                        editModeActive={isInEditMode}
                        handleScrollToErrors={() => handleScrollToErrors(errors)}
                        infoLink={{
                            type: t("Titles.Title").toLowerCase(),
                            link: "https://helpdesk.crealo.app/fr/article/creer-un-nouveau-titre-1s7geb2/",
                        }}
                    />
                </div>
            </FormProvider>
        </>
    );
};

export default TitleFormContainer;
